import { type AnyStateMachine } from 'xstate';
// Pinia 스토어 (Firestore 로딩용 - 향후 분리 필요)
// 분리된 모듈 import
import { FirebaseFileService } from './services/file.service';
import { FirebaseChatService } from './services/chat.service';
import { createAuthorizeActorLogic, createUploadFileActorLogic, type CustomMachineContext, internalMachineEvents, createWowpressMachineFactory, type StateMachineMetaData, type StateMachineDefinition } from '@wowpress/state-machine';
import { FirebaseAuthService } from './services/auth.service';
import { getFirestore, doc, getDoc } from 'firebase/firestore';


const fileService = new FirebaseFileService();
const chatService = new FirebaseChatService();
const authService = new FirebaseAuthService();

export const previewUploadFileActor = createUploadFileActorLogic(fileService);
export const previewAuthorizeActor = createAuthorizeActorLogic(authService);


/**
 * Firestore에서 상태 머신 정의를 로드합니다.
 * @param machineId 로드할 상태 머신의 고유 ID
 * @returns 로드된 상태 머신 데이터 또는 실패 시 null
 */
async function loadStateMachineDefinition(machineId: string): Promise<(StateMachineMetaData & { definition: StateMachineDefinition; id: string }) | null> {
    try {
        const stateMachinesCollection = 'stateMachines';
        const db = getFirestore()
        const machineDocRef = doc(db, stateMachinesCollection, machineId);
        console.log(`Firestore에서 문서 로드 중: ${stateMachinesCollection}/${machineId}`);

        const docSnap = await getDoc(machineDocRef);
        if (docSnap.exists()) {
            console.log(`✅ Firestore에서 '${stateMachinesCollection}/${machineId}' 문서를 성공적으로 로드했습니다.`);
            const data = docSnap.data() as (StateMachineMetaData & { definition: StateMachineDefinition });
            // definition 필드가 실제로 있는지 확인
            if (!data?.definition) {
                console.warn(`[Store] Firestore 문서(${machineId})에 'definition' 필드가 없습니다.`);
                return null; // 정의가 없으면 null 반환
            }
            // 로드된 데이터와 ID 반환
            const loadedData = { ...data, id: machineId };
            console.log('[XState] 로드된 상태 머신:', loadedData);
            return loadedData;
        } else {
            console.log(`문서 '${stateMachinesCollection}/${machineId}'를 찾을 수 없습니다.`);
            return null; // 문서가 없으면 null 반환
        }
    } catch (err) {
        console.error(`❌ Firestore에서 문서 로드 중 오류 발생 (${machineId}):`, err);
        return null; // 오류 발생 시 null 반환
    }
}

/**
 * Firestore에서 상태 머신 정의를 로드하고 XState 머신 인스턴스를 생성합니다.
 * @param machineId Firestore에서 로드할 상태 머신 ID (기본값: 'wowpressBot')
 * @param initialContextOverrides 머신 생성 시 기본 컨텍스트를 덮어쓸 값
 * @param initialEventOverrides 머신 생성 시 기본 이벤트를 덮어쓸 값 (on 블록)
 * @param initialStateOverrides 머신 생성 시 초기 상태를 덮어쓸 값
 * @returns 생성된 XState 머신 또는 로드 실패 시 null
 */
export async function createWowpressCustomerMachine(
    initialContextOverrides?: Partial<CustomMachineContext>,
    initialStateOverrides?: string | null,
): Promise<AnyStateMachine | null> {
    const machineId = 'wowpressBot';
    const loadedStateMachine = await loadStateMachineDefinition(machineId);

    if (!loadedStateMachine?.definition) {
        console.error(`[XState] Firestore에서 상태 머신 정의 로드 실패 또는 정의 없음: ${machineId}.`);
        return null;
    }
    console.log(`[XState] Firestore 정의 로드 성공: ${machineId}. 머신 생성 중...`);
    const loadedMachineConfig = loadedStateMachine.definition as StateMachineDefinition; // 이미 타입이 StateMachineDefinition으로 가정됨

    // 컨텍스트 및 설정 병합
    const finalInitialContext: CustomMachineContext = {
        ...(loadedMachineConfig.context as CustomMachineContext || {
            orderInfo: null,
            showLoginModal: false
        }),
        ...(initialContextOverrides || {}),
    };
    if ('currentBotMessage' in finalInitialContext) {
        delete (finalInitialContext as any).currentBotMessage;
        console.log("[XState] Removed 'currentBotMessage' from final initial context.");
    }
    const finalOnEvents = {
        ...(loadedMachineConfig.on || {}),
        ...(internalMachineEvents || {}),
    };
    const machineConfigForCreation = {
        ...loadedMachineConfig,
        context: finalInitialContext,
        on: finalOnEvents,
        // loadedStateMachine에서 id도 가져와서 설정에 포함 (필요하다면)
        id: loadedStateMachine.id, // id 추가
    };
    if (initialStateOverrides) {
        machineConfigForCreation.initial = initialStateOverrides;
        console.log("[XState] 초기 상태 오버라이드:", initialStateOverrides);
    }

    console.log("[XState] 동적 상태 머신 생성 준비 완료");

    try {
        // 서비스 인스턴스 생성

        // Refactored: Use the factory to get implementations
        const implementations = createWowpressMachineFactory(fileService, authService, chatService);

        // 머신 생성 (설정 + 구현체)
        const dynamicMachine = implementations.createMachine(machineConfigForCreation);

        console.log(`[XState] 동적 상태 머신 생성 성공: ${machineId}`);
        return dynamicMachine;
    } catch (creationError) {
        console.error(`[XState] 로드된 정의로 머신 생성 중 오류 발생: ${machineId}`, creationError);
        console.error("[XState] 오류 발생 시 사용된 설정:", JSON.stringify(machineConfigForCreation, null, 2));
        throw creationError; // 오류를 다시 throw하여 호출자가 처리하도록 함
    }
}
